import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Image, Input, Loading, Select, Table, Tag } from "@stonelog/stonelog-design-system";
import { TagCustom } from "@components/TagCustom";
import { GingaIcon } from "@stonelog/stonelog-ginga-icons";
import { usePaginationConfig } from "@hooks/usePaginationConfig";
import { CloseCircleIcon } from "@assets/icons/CloseCircleIcon";
import { DatePickerCustom } from "@components/DatePickerCustom";
import { useDispatch, useSelector } from "react-redux";
import {
    inventoryAuditSelector,
    getInventoryAudit,
    resetInventoryAuditImageStatus,
    getInventoryAuditImageContent
} from '../../../../features/anexos/inventoryAuditSlice';

import { formatDate } from "../../../../utils/formatter";
import { compareTableColumn } from "@utils/common";
import { saveAs } from "file-saver";
import Empty from "@assets/icons/errorBookImage.svg";
import * as XLSX from "xlsx";
import moment from "moment";

import * as S from "./styles";


export const InventoryAudit = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const [startDate, setStartDate] = useState(firstDayOfMonth);
    const [endDate, setEndDate] = useState(lastDayOfMonth);
    const [serialActiveTags, setSerialActiveTags] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [poloSelected, setPoloSelected] = useState(null);
    const [rowClickedInfos, setRowClickedInfos] = useState(null);

    const paginationConfig = usePaginationConfig([10, 10]);
    const dispatch = useDispatch();

    const {
        getInventoryAuditTableData,
        getInventoryAuditTableDataStatus,
        getInventoryAuditImage,
        getInventoryAuditImageStatus
    } = useSelector(inventoryAuditSelector);
    console.log(getInventoryAuditTableData)
    const columns = [
        {
            title: 'Número da OS',
            dataIndex: 'os',
            key: 'os',
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "os"),
            },
        },
        {
            title: 'Serial green app',
            dataIndex: 'serialEstoque',
            key: 'serialEstoque',
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "serialEstoque"),
            },
        },
        {
            title: "Situação do serial",
            dataIndex: "serialIsDivergente",
            key: "serialIsDivergente",
            render: (value, record) => (
                record.serialIsDivergente ? (
                    <Tag text="Divergente" type="error" />
                ) : (
                    <Tag text="Sem divergência" type="success" />
                )
            ),
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "serialIsDivergente"),
            },
        },
        {
            title: 'Serial baixado',
            dataIndex: 'serialOS',
            key: 'serialOS',
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "serialOS"),
            },
        },
        {
            title: 'Data de saída de inventário',
            dataIndex: 'dataInventario',
            key: 'dataInventario',
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "dataInventario"),
            },
        },
        {
            title: 'Polo',
            dataIndex: 'polo',
            key: 'polo',
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "polo"),
            },
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value, record) => (
                <Tag text={record.status} type="info" />
            ),
            sorter: {
                compare: (a, b) => compareTableColumn(a, b, "status"),
            },
        },
    ];

    const dataSource = useMemo(() => {
        return getInventoryAuditTableData?.map((item, idx) => ({
            key: idx,
            os: item.order_number,
            serialEstoque: item.serial_number,
            dataInventario: formatDate(item.modification_date),
            polo: item.provider,
            fotoSerial: item.serial_photo || '',
            serialIsDivergente: item.is_divergent,
            serialOS: item.serial_os,
            status: item.status,
        })) || [];
    }, [getInventoryAuditTableData]);

    const filteredDataSource = useMemo(() => {
        if (!dataSource) return [];

        let filtered = dataSource;

        if (serialActiveTags.length > 0) {
            const tagValues = serialActiveTags.map(tag => tag.value.toUpperCase());
            filtered = filtered.filter(item => tagValues.includes(item.serialEstoque.toUpperCase()));
        }

        if (poloSelected) {
            filtered = filtered.filter(item => item.polo === poloSelected);
        }

        return filtered;
    }, [serialActiveTags, dataSource, poloSelected]);

    const poloOptions = useMemo(() => {
        const polosUnicos = new Set(getInventoryAuditTableData?.map(item => item.provider));
        return Array.from(polosUnicos).map(polo => ({ value: polo, label: polo }));
    }, [getInventoryAuditTableData]);

    const handleOnEnter = () => {
        const raw = inputValue.trim();
        if (!raw) return;
        const serials = raw.split(/[,\s;]+/).map((v) => v.trim().toUpperCase())
        setSerialActiveTags((prev) => {
            const existing = new Set(prev.map((t) => t.value));
            const toAdd = serials.filter((v) => !existing.has(v)).map((v) => ({ value: v }));
            return toAdd.length ? [...prev, ...toAdd] : prev;
        });
        setInputValue("");
    };

    const handleExportExcel = () => {
        if (!filteredDataSource.length) return;
        const exportData = filteredDataSource.map(({ os, serialEstoque, serialOS, dataInventario, polo, serialIsDivergente }) => ({
            "Número da OS": os,
            "Serial Estoque": serialEstoque,
            "Serial OS": serialOS,
            "Data de Inventário": dataInventario,
            "Polo": polo,
            "Situação": serialIsDivergente ? "Divergente" : "Sem Divergência"
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "AuditoriaInventario");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

        saveAs(blob, `auditoria-inventario-${selectedMonth.value}.xlsx`);
    };

    const onChangeInitialDate = (date, dateString) => {
        setStartDate(date ? date.toDate() : null);
        setEndDate(null)
    };

    const onChangeFinalDate = (date, dateString) => {
        setEndDate(date ? date.toDate() : null);
    };

    const disabledEndDate = (current) => {
        return current && startDate && current < moment(startDate).startOf('day');
    };

    useEffect(() => {
        if (startDate && endDate) {
            const params = {
                data_inicial: moment(startDate).format('YYYY-MM-DD'),
                data_final: moment(endDate).format('YYYY-MM-DD'),
            };
            dispatch(getInventoryAudit(params));
            dispatch(resetInventoryAuditImageStatus());
        }
    }, [startDate, endDate, dispatch]);

    useEffect(() => {
        if (rowClickedInfos) {
            dispatch(getInventoryAuditImageContent({ serial_photo: rowClickedInfos.fotoSerial }));
        }
    }, [rowClickedInfos, dispatch]);

    return (
        <>
            {(rowClickedInfos && getInventoryAuditImageStatus !== "pending") ? (
                <Image
                    src={getInventoryAuditImage || Empty}
                    width={2}
                    visible={!!rowClickedInfos}
                    setVisible={setRowClickedInfos}
                />
            ) : (
                getInventoryAuditImageStatus === "pending" && (
                    <S.LoadingMask>
                        <Loading />
                    </S.LoadingMask>
                )
            )}
            <Card.Root>
                <Card.Header>
                    <Card.HeaderText
                        title="Auditoria de inventário"
                        subtitle="Acompanhe a auditoria de inventário das máquinas e, para mais detalhes, clique no nome para visualizar a foto do serial atrelado à ordem de serviço."
                    />
                    <Card.HeaderActions className="ginga-lib-card-header-actions">
                        <Button height="44px" onClick={handleExportExcel} disabled={!filteredDataSource.length}>
                            <GingaIcon name="action-download" color="#303742" size={'20'} />
                        </Button>
                    </Card.HeaderActions>
                </Card.Header>

                <S.InventaryFilterContainer>
                    <S.TopInventaryFilterContent>
                        <Select
                            prefixIcon={<GingaIcon name="location" color="rgba(118, 127, 141, 1)" size={21} />}
                            placeholder="Selecione um polo"
                            label="Polo"
                            size="small"
                            value={poloSelected}
                            onChange={(val) => setPoloSelected(val)}
                            options={poloOptions}
                            allowClear
                        />
                        <Input
                            size="small"
                            label="Serial estoque"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onPressEnter={handleOnEnter}
                            placeholder="Digite o serial"
                            clear
                        />
                        <DatePickerCustom
                            label={"Início"}
                            value={startDate ? moment(startDate) : null}
                            onChange={onChangeInitialDate}
                            format={"DD/MM/YYYY"}
                            placeholder={"DD/MM/AAAA"}
                            allowClear
                        />
                        <DatePickerCustom
                            label={"Final"}
                            value={endDate ? moment(endDate) : null}
                            onChange={onChangeFinalDate}
                            format={"DD/MM/YYYY"}
                            placeholder={"DD/MM/AAAA"}
                            disabled={!startDate}
                            disabledDate={disabledEndDate}
                            allowClear
                        />
                    </S.TopInventaryFilterContent>
                    {serialActiveTags.length > 0 && (
                        <S.TopInventaryFilterTagContent>
                            <TagCustom tags={serialActiveTags} setTags={setSerialActiveTags} closable closeIcon={<CloseCircleIcon size={16} />} />
                        </S.TopInventaryFilterTagContent>
                    )}
                </S.InventaryFilterContainer>

                <Card.Body className="ginga-lib-card-body">
                    <Table
                        paginationConfig={paginationConfig}
                        columns={columns}
                        dataSource={filteredDataSource}
                        className="ginga-lib-table"
                        onRow={(row) => ({
                            onClick: () => {
                                setRowClickedInfos(row);
                            },
                        })}
                        loading={getInventoryAuditTableDataStatus === 'pending'}
                    />
                </Card.Body>
            </Card.Root>
        </>
    );
};
